import React from 'react';

import { DashboardListPage, DashboardViewPage } from '@sms/dashboard-dashboard-ui';
import { DecisionTableCreatePage, DecisionTableEditPage, DecisionTableListPage } from '@sms/decision-table-decision-ui';
import {
  BuildNewProcessPage,
  DeployProcessPage,
  DocumentationProcessPage,
  EditProcessPage,
  InstanceProcessPage,
  MonitorProcessPage,
  ProcessesPage,
  StartProcessPage,
} from '@sms/fluere-process-fluere-process-ui';
import { CreateRulePage, EditRulePage, RuleListPage } from '@sms/journal-cep-journal-cep-ui';
import {
  CreateEventPage,
  CreateEventTemplatePageWithProvider,
  EditEventPage,
  EditEventTemplatePageWithProvider,
  EventListPage,
  EventTemplateListPage,
  EventViewPage,
} from '@sms/journal-event-journal-event-ui';
import {
  TaskPage,
  TaskListPage,
  CreateTaskPage,
  EditTaskPage,
  CreateTemplatePage as CreateTaskTemplatePage,
  EditTemplatePage as EditTaskTemplatePage,
  TemplatesListPage as TaskTemplatesListPage,
} from '@sms/journal-task-journal-task-ui';
import { CategoryListPage } from '@sms/category-category-ui';
import { PageConfig } from '@sms/plasma-ui';
import { CreateReportPage, CreateTemplatePage, EditTemplatePage, MainPage, TemplatesPage } from '@sms/report-report-ui';

import app from '../config/app.json';
import { PageWithMenu } from './PageWithMenu';
import RootPage from './RootPage';
// import { TestePlasma } from './TestePlasma';

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
const SUB_PATH = (window as any).plasma?.environment?.SUB_PATH ?? '/';
const PROCESSED_SUB_PATH = SUB_PATH.replace(/\/$/, '');

const withMenu = (PageComponent: React.ComponentType<any>) => {
  return (props: any) => (
    <PageWithMenu>
      <PageComponent {...props} />
    </PageWithMenu>
  );
};

function prefixPath(path: string) {
  const treatedPath = path.replace(/^\//, '');
  return `${PROCESSED_SUB_PATH}/${treatedPath}`.replace(/\/\//g, '/');
}

const pages: PageConfig[] = [
  {
    key: 'Root',
    path: '/',
    page: RootPage,
  },
  // {
  //   key: 'TestePlasma',
  //   path: prefixPath(`/${app.code}/teste-plasma`),
  //   page: TestePlasma,
  // },
  {
    key: 'RootAppCode',
    path: prefixPath(`/${app.code}`),
    page: RootPage,
  },
  {
    key: 'Processes',
    path: prefixPath(`/${app.code}/processes`),
    page: withMenu(ProcessesPage),
    title: 'app.menu.processes',
    permissions: ['process.view'],
  },
  {
    key: 'NewProcesses',
    path: prefixPath(`/${app.code}/processes/new`),
    page: withMenu(BuildNewProcessPage),
    title: 'app.menu.newProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'DeployProcesses',
    path: prefixPath(`/${app.code}/processes/deploy/:modelId`),
    page: withMenu(DeployProcessPage),
    title: 'app.menu.deployProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'StartProcess',
    path: prefixPath(`/${app.code}/processes/start/:id`),
    page: withMenu(StartProcessPage),
    title: 'app.menu.startProcess',
    permissions: ['process.edit'],
  },
  {
    key: 'DocumentationProcessPage',
    path: prefixPath(`/${app.code}/processes/documentation/:id`),
    page: withMenu(DocumentationProcessPage),
    title: 'app.menu.documentationProcess',
    permissions: ['process.view'],
  },
  {
    key: 'MonitorProcessPage',
    path: prefixPath(`/${app.code}/processes/monitor/:id`),
    page: withMenu(MonitorProcessPage),
    title: 'app.menu.monitorProcess',
    permissions: ['process.view'],
  },
  {
    key: 'InstanceProcessPage',
    path: prefixPath(`/${app.code}/processes/monitor/:id/instance/:instanceId`),
    page: withMenu(InstanceProcessPage),
    title: 'app.menu.instanceProcess',
    permissions: ['process.view'],
  },
  {
    key: 'EditProcessPage',
    path: prefixPath(`/${app.code}/processes/edit/:id`),
    page: withMenu(EditProcessPage),
    title: 'app.menu.editProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'TaskList',
    path: prefixPath(`/${app.code}/tasks`),
    page: withMenu(TaskListPage),
    title: 'app.menu.tasks',
    permissions: ['task.view'],
  },
  {
    key: 'TaskTemplatesList',
    path: prefixPath(`/${app.code}/tasks/settings/templates`),
    page: withMenu(TaskTemplatesListPage),
    permissions: ['task.template.view'],
  },
  {
    key: 'CreateTaskTemplate',
    path: prefixPath(`/${app.code}/tasks/settings/templates/new`),
    page: withMenu(CreateTaskTemplatePage),
    permissions: ['task.template.edit'],
  },
  {
    key: 'EditTaskTemplate',
    path: prefixPath(`/${app.code}/tasks/settings/templates/edit/:id`),
    page: withMenu(EditTaskTemplatePage),
    permissions: [['task.template.view']],
  },
  {
    key: 'Task',
    path: prefixPath(`/${app.code}/tasks/execute/:taskId`),
    page: withMenu(TaskPage),
    permissions: ['task.view'],
  },
  {
    key: 'CreateTask',
    path: prefixPath(`/${app.code}/tasks/create`),
    page: withMenu(CreateTaskPage),
    permissions: ['task.edit'],
  },
  {
    key: 'EditTask',
    path: prefixPath(`/${app.code}/tasks/edit/:taskId`),
    page: withMenu(EditTaskPage),
    permissions: ['task.edit'],
  },
  {
    key: 'Report',
    path: prefixPath(`/${app.code}/reports`),
    page: withMenu(MainPage),
    title: 'app.menu.reports',
    permissions: ['report.view'],
  },
  {
    key: 'NewReport',
    path: prefixPath(`/${app.code}/reports/new`),
    page: withMenu(CreateReportPage),
    title: 'app.menu.reports',
    permissions: ['report.edit'],
  },
  {
    key: 'ReportTemplate',
    path: prefixPath(`/${app.code}/reports/settings/template`),
    page: withMenu(TemplatesPage),
    title: 'app.menu.reports',
    permissions: ['report.template.view'],
  },
  {
    key: 'NewReportTemplate',
    path: prefixPath(`/${app.code}/reports/settings/template/new`),
    page: withMenu(CreateTemplatePage),
    title: 'app.menu.reports',
    permissions: ['report.template.edit'],
  },
  {
    key: 'EditReportTemplate',
    path: prefixPath(`/${app.code}/reports/settings/template/edit/:id`),
    page: withMenu(EditTemplatePage),
    title: 'app.menu.reports',
    permissions: ['report.template.view'],
  },
  {
    key: 'EventList',
    path: prefixPath(`/${app.code}/events`),
    page: withMenu(EventListPage),
    permissions: ['event.view'],
  },
  {
    key: 'EventTemplates',
    path: prefixPath(`/${app.code}/events/settings/templates`),
    page: withMenu(EventTemplateListPage),
    permissions: ['event.template.view'],
  },
  {
    key: 'CreateEventTemplates',
    path: prefixPath(`/${app.code}/events/settings/templates/create`),
    page: withMenu(CreateEventTemplatePageWithProvider),
    permissions: ['event.template.edit'],
  },
  {
    key: 'EditEventTemplates',
    path: prefixPath(`/${app.code}/events/settings/templates/edit/:id`),
    page: withMenu(EditEventTemplatePageWithProvider),
    permissions: ['event.template.view'],
  },
  {
    key: 'EventCreate',
    path: prefixPath(`/${app.code}/events/create`),
    page: withMenu(CreateEventPage),
    permissions: ['event.edit'],
  },
  {
    key: 'EventEdit',
    path: prefixPath(`/${app.code}/events/:id/edit`),
    page: withMenu(EditEventPage),
    permissions: ['event.edit'],
  },
  {
    key: 'EventView',
    path: prefixPath(`/${app.code}/events/:id`),
    page: withMenu(EventViewPage),
    permissions: ['event.view'],
  },
  {
    key: 'RuleList',
    path: prefixPath(`/${app.code}/rules`),
    page: withMenu(RuleListPage),
    permissions: ['rule.view'],
  },
  {
    key: 'CreateRule',
    path: prefixPath(`/${app.code}/rules/create`),
    page: withMenu(CreateRulePage),
    permissions: ['rule.edit'],
  },
  {
    key: 'EditRule',
    path: prefixPath(`/${app.code}/rules/edit/:id`),
    page: withMenu(EditRulePage),
    permissions: ['rule.edit'],
  },
  {
    key: 'ViewRule',
    path: prefixPath(`/${app.code}/rules/view/:id`),
    page: withMenu(EditRulePage),
    permissions: ['rule.view'],
  },
  {
    key: 'Decision tables list',
    path: prefixPath(`/${app.code}/decisionTables`),
    page: withMenu(DecisionTableListPage),
    permissions: ['decision.view'],
  },
  {
    key: 'Edit decision table',
    path: prefixPath(`/${app.code}/decisionTables/edit/:id`),
    page: withMenu(DecisionTableEditPage),
    permissions: ['decision.view'],
  },
  {
    key: 'Create decision table',
    path: prefixPath(`/${app.code}/decisionTables/create`),
    page: withMenu(DecisionTableCreatePage),
    permissions: ['decision.edit'],
  },
  {
    key: 'BoardList',
    path: prefixPath(`/${app.code}/boards`),
    page: withMenu(DashboardListPage),
    permissions: ['dashboard.view'],
  },
  {
    key: 'BoardRule',
    path: prefixPath(`/${app.code}/boards/view/:id`),
    page: withMenu(DashboardViewPage),
    permissions: ['dashboard.view'],
  },
  {
    key: 'TaskCategoriesList',
    path: prefixPath(`/${app.code}/tasks/settings/categories`),
    page: withMenu(CategoryListPage),
    permissions: ['category.view'],
  },
  {
    key: 'EventCategoriesList',
    path: prefixPath(`/${app.code}/events/settings/categories`),
    page: withMenu(CategoryListPage),
    permissions: ['category.view'],
  },
  {
    key: 'ReportCategoriesList',
    path: prefixPath(`/${app.code}/reports/settings/categories`),
    page: withMenu(CategoryListPage),
    permissions: ['category.view'],
  }
];

export default pages;
